import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CompanyRequest } from "@/types/api";

const companySchema = z.object({
  name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  cnpj: z.string().min(14, "CNPJ deve ter 14 dígitos").max(18, "CNPJ inválido"),
  telephone: z.string().optional(),
});

type CompanyFormData = z.infer<typeof companySchema>;

interface CompanyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (data: CompanyRequest) => void;
}

const CompanyModal = ({ open, onOpenChange, onSave }: CompanyModalProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: "",
      cnpj: "",
      telephone: "",
    },
  });

  const formatCNPJ = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    return numbers
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .substring(0, 18);
  };

  const onSubmit = async (data: CompanyFormData) => {
    setIsLoading(true);
    try {
      if (onSave) {
        // Remove formatação do CNPJ para enviar apenas números
        const cleanCnpj = data.cnpj.replace(/\D/g, "");
        onSave({
          name: data.name,
          cnpj: cleanCnpj,
          telephone: data.telephone,
        });
      }

      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao criar empresa:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Nova Empresa</DialogTitle>
          <DialogDescription>
            Cadastre uma nova empresa no sistema
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="name">Nome da Empresa</Label>
            <Input
              id="name"
              {...form.register("name")}
              className="mt-1"
              placeholder="Digite o nome da empresa"
            />
            {form.formState.errors.name && (
              <p className="text-sm text-red-500 mt-1">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="cnpj">CNPJ</Label>
            <Input
              id="cnpj"
              {...form.register("cnpj")}
              onChange={(e) => {
                const formatted = formatCNPJ(e.target.value);
                form.setValue("cnpj", formatted);
              }}
              className="mt-1"
              placeholder="00.000.000/0000-00"
              maxLength={18}
            />
            {form.formState.errors.cnpj && (
              <p className="text-sm text-red-500 mt-1">
                {form.formState.errors.cnpj.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="telephone">Telefone</Label>
            <Input
              id="telephone"
              {...form.register("telephone")}
              className="mt-1"
              placeholder="(00) 00000-0000"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Salvando..." : "Criar Empresa"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyModal;
